/* THEBLACKBOXHUB — gallery.js
   Wall colour, "My wall" (saved posters), cart count + nail bump, hero lamp, room preview.
   Vanilla JS, no dependencies. Loads after global.js. */
(function () {
  'use strict';

  const theme = window.theme || { routes: {}, strings: {} };
  theme.routes = theme.routes || {};
  theme.strings = theme.strings || {};
  const utils = window.themeUtils || {};

  const WALLS = ['chalk', 'indigo', 'slate', 'sage', 'brick', 'sand'];
  const WALL_KEY = 'tbbh-wall';
  const SAVED_KEY = 'tbbh-saved';
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function define(name, ctor) {
    if (!customElements.get(name)) customElements.define(name, ctor);
  }

  function esc(str) {
    return utils.escapeHtml ? utils.escapeHtml(str) : String(str == null ? '' : str);
  }

  function rootPath() {
    return (theme.routes.root || '/').replace(/\/$/, '');
  }

  /* ---------- Wall colour ---------- */
  function getWall() {
    return document.documentElement.getAttribute('data-wall') || 'indigo';
  }

  function syncWallUI() {
    const wall = getWall();
    document.querySelectorAll('[data-wall-option]').forEach(function (button) {
      button.setAttribute('aria-checked', button.getAttribute('data-wall-option') === wall ? 'true' : 'false');
    });
  }

  function setWall(wall) {
    if (WALLS.indexOf(wall) < 0) return;
    document.documentElement.setAttribute('data-wall', wall);
    try {
      window.localStorage.setItem(WALL_KEY, wall);
    } catch (e) {
      /* storage unavailable: the wall still changes for this visit */
    }
    syncWallUI();
    document.dispatchEvent(new CustomEvent('wall:change', { detail: { wall: wall } }));
  }

  document.addEventListener('click', function (event) {
    const option = event.target.closest('[data-wall-option]');
    if (option) setWall(option.getAttribute('data-wall-option'));
  });

  document.addEventListener('keydown', function (event) {
    const option = event.target.closest && event.target.closest('[data-wall-option]');
    if (!option || (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft')) return;
    event.preventDefault();
    const group = option.closest('[role="radiogroup"]');
    const items = group ? Array.from(group.querySelectorAll('[data-wall-option]')) : [];
    const index = items.indexOf(option);
    const next = items[(index + (event.key === 'ArrowRight' ? 1 : -1) + items.length) % items.length];
    if (next) {
      next.focus();
      setWall(next.getAttribute('data-wall-option'));
    }
  });

  /* ---------- Saved posters ("My wall") ---------- */
  function getSaved() {
    try {
      const value = JSON.parse(window.localStorage.getItem(SAVED_KEY) || '[]');
      return Array.isArray(value) ? value : [];
    } catch (e) {
      return [];
    }
  }

  function writeSaved(list) {
    try {
      window.localStorage.setItem(SAVED_KEY, JSON.stringify(list));
    } catch (e) {
      /* ignore */
    }
  }

  function syncSavedUI() {
    const list = getSaved();
    document.querySelectorAll('[data-wall-save]').forEach(function (button) {
      const on = list.indexOf(button.getAttribute('data-wall-save')) > -1;
      button.setAttribute('aria-pressed', on ? 'true' : 'false');
      const text = button.querySelector('[data-wall-save-text]');
      if (text) {
        const label = on ? button.getAttribute('data-label-saved') : button.getAttribute('data-label-save');
        if (text.textContent !== label) text.textContent = label;
      }
    });
    document.querySelectorAll('[data-wall-count]').forEach(function (el) {
      const value = String(list.length);
      if (el.textContent !== value) el.textContent = value;
      el.hidden = list.length === 0;
    });
  }

  function toggleSaved(handle) {
    let list = getSaved();
    const index = list.indexOf(handle);
    if (index > -1) list.splice(index, 1);
    else list.unshift(handle);
    list = list.slice(0, 40);
    writeSaved(list);
    syncSavedUI();
    document.dispatchEvent(new CustomEvent('wall:saved'));
    return index === -1;
  }

  document.addEventListener('click', function (event) {
    const button = event.target.closest('[data-wall-save]');
    if (!button) return;
    event.preventDefault();
    const added = toggleSaved(button.getAttribute('data-wall-save'));
    if (utils.showToast) utils.showToast(added ? theme.strings.savedToWall : theme.strings.removedFromWall);
  });

  /* Drawer that lists saved posters */
  if (window.ThemeDrawer) {
    class WallDrawer extends window.ThemeDrawer {
      connectedCallback() {
        super.connectedCallback();
        if (this._wallBound) return;
        this._wallBound = true;
        this.listEl = this.querySelector('[data-wall-list]');
        this.emptyEl = this.querySelector('[data-wall-empty]');
        this.statusEl = this.querySelector('[data-wall-status]');
        document.addEventListener('click', (event) => {
          const trigger = event.target.closest('[data-wall-open]');
          if (!trigger) return;
          event.preventDefault();
          this.open(trigger);
          this.render();
        });
        document.addEventListener('wall:saved', () => {
          if (this.isOpen) this.render();
        });
        this.listEl.addEventListener('click', (event) => {
          const remove = event.target.closest('[data-wall-remove]');
          if (!remove) return;
          toggleSaved(remove.getAttribute('data-wall-remove'));
        });
      }

      render() {
        const handles = getSaved();
        this.statusEl.textContent = '';
        this.emptyEl.hidden = handles.length > 0;
        if (!handles.length) {
          this.listEl.innerHTML = '';
          return;
        }
        Promise.all(
          handles.map(function (handle) {
            return fetch(rootPath() + '/products/' + encodeURIComponent(handle) + '.js')
              .then(function (r) {
                if (r.status === 404) return { missing: handle };
                if (!r.ok) throw new Error('bad response');
                return r.json();
              });
          })
        )
          .then((items) => {
            const missing = items.filter(function (i) { return i && i.missing; }).map(function (i) { return i.missing; });
            if (missing.length) writeSaved(getSaved().filter(function (h) { return missing.indexOf(h) === -1; }));
            const products = items.filter(function (i) { return i && !i.missing; });
            this.emptyEl.hidden = products.length > 0;
            this.listEl.innerHTML = products.map(function (p) {
              const img = p.featured_image
                ? '<img src="' + esc(p.featured_image + (p.featured_image.indexOf('?') > -1 ? '&' : '?') + 'width=300') + '" alt="" width="300" height="375" loading="lazy">'
                : '';
              const price = utils.formatMoney ? utils.formatMoney(p.price) : String(p.price / 100);
              return (
                '<li class="wall-item">' +
                '<a class="wall-item__media" href="' + esc(p.url) + '" tabindex="-1" aria-hidden="true">' + img + '</a>' +
                '<div><a class="wall-item__title" href="' + esc(p.url) + '">' + esc(p.title) + '</a>' +
                '<span class="price"><span class="price__regular">' + esc(price) + '</span></span>' +
                '<div class="wall-item__actions"><a class="link" href="' + esc(p.url) + '">' + esc(theme.strings.wallView) + '</a>' +
                '<button type="button" class="link" data-wall-remove="' + esc(p.handle) + '">' + esc(theme.strings.wallRemove) + '</button></div></div>' +
                '</li>'
              );
            }).join('');
          })
          .catch(() => {
            this.statusEl.textContent = theme.strings.wallLoadError || '';
          });
      }
    }
    define('wall-drawer', WallDrawer);
  }

  /* ---------- Cart count + nail bump ---------- */
  function bump() {
    if (reduced) return;
    document.querySelectorAll('#cart-icon-link .icon, .bottom-bar [data-cart-count], .cart-count-bubble').forEach(function (el) {
      el.classList.remove('is-bumped');
      void el.offsetWidth;
      el.classList.add('is-bumped');
      setTimeout(function () { el.classList.remove('is-bumped'); }, 800);
    });
  }

  function refreshCartCount() {
    if (!theme.routes.cart) return;
    fetch(theme.routes.cart + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-cart-count]').forEach(function (el) {
          const value = String(cart.item_count);
          if (el.textContent !== value) el.textContent = value;
          el.hidden = cart.item_count === 0;
        });
      })
      .catch(function () { /* the cart drawer still shows the right count */ });
  }

  if (utils.subscribe) {
    utils.subscribe('cart:updated', function () {
      bump();
      refreshCartCount();
    });
  }

  /* ---------- Home hero: the lamp switches on ---------- */
  function initHeroes(root) {
    (root || document).querySelectorAll('.gallery-hero:not([data-ready])').forEach(function (hero) {
      hero.setAttribute('data-ready', 'true');
      const designMode = window.Shopify && window.Shopify.designMode;
      if (reduced || designMode) hero.classList.add('is-lit');
      else requestAnimationFrame(function () { setTimeout(function () { hero.classList.add('is-lit'); }, 120); });

      const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      if (hero.getAttribute('data-spotlight') === 'true' && fine && !reduced) {
        hero.addEventListener('pointermove', function (event) {
          const rect = hero.getBoundingClientRect();
          hero.style.setProperty('--mx', event.clientX - rect.left + 'px');
          hero.style.setProperty('--my', event.clientY - rect.top + 'px');
          hero.classList.add('is-tracking');
        });
        hero.addEventListener('pointerleave', function () {
          hero.classList.remove('is-tracking');
        });
      }
    });
  }

  /* ---------- Room preview ---------- */
  const A_SIZES = { 0: [84.1, 118.9], 1: [59.4, 84.1], 2: [42, 59.4], 3: [29.7, 42], 4: [21, 29.7], 5: [14.8, 21] };
  const PX_PER_CM = 266 / 200;
  const CENTER_X = 203;
  const CENTER_Y = 79;

  function parseSize(text) {
    text = String(text || '');
    let m = text.match(/\bA\s?([0-5])\b/i);
    if (m) {
      const dims = A_SIZES[m[1]];
      return { name: 'A' + m[1], w: dims[0], h: dims[1] };
    }
    m = text.match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i);
    if (m) {
      let w = parseFloat(m[1]);
      let h = parseFloat(m[2]);
      if (/in\b|inch|"|″/i.test(text)) {
        w *= 2.54;
        h *= 2.54;
      }
      return { name: '', w: w, h: h };
    }
    return null;
  }

  function fmt(n) {
    return String(Math.round(n * 10) / 10).replace(/\.0$/, '');
  }

  class RoomPreview extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.frame = this.querySelector('[data-room-frame]');
      this.image = this.querySelector('[data-room-image]');
      this.caption = this.querySelector('[data-room-caption]');
      this.template = this.getAttribute('data-caption') || '[size], [dims]';
      this.apply(this.getAttribute('data-start'));
      const info = this.closest('product-info');
      if (info) {
        info.addEventListener('variant:change', (event) => {
          const variant = event.detail && event.detail.variant;
          this.apply(variant ? variant.title : '');
          const placard = document.querySelector('[data-placard-size]');
          if (placard && variant) placard.textContent = variant.title;
        });
      }
    }

    apply(title) {
      if (!this.frame) return;
      const size = parseSize(title) || { name: 'A3', w: 29.7, h: 42 };
      const w = Math.min(size.w * PX_PER_CM, 170);
      const h = Math.min(size.h * PX_PER_CM, 200);
      const x = CENTER_X - w / 2;
      const y = Math.max(CENTER_Y - h / 2, 6);
      this.frame.setAttribute('x', x);
      this.frame.setAttribute('y', y);
      this.frame.setAttribute('width', w);
      this.frame.setAttribute('height', h);
      if (this.image) {
        this.image.setAttribute('x', x + 3);
        this.image.setAttribute('y', y + 3);
        this.image.setAttribute('width', Math.max(w - 6, 1));
        this.image.setAttribute('height', Math.max(h - 6, 1));
      }
      if (this.caption) {
        const dimsText = fmt(size.w) + ' x ' + fmt(size.h) + ' cm';
        const label = size.name ? size.name + ', ' + dimsText : dimsText;
        this.caption.textContent = this.template.indexOf('[size], [dims]') > -1
          ? this.template.replace('[size], [dims]', label)
          : this.template.replace('[size]', size.name).replace('[dims]', dimsText);
      }
    }
  }
  define('room-preview', RoomPreview);

  /* ---------- Boot ---------- */
  let scheduled = false;
  function syncAll() {
    syncWallUI();
    syncSavedUI();
  }

  function boot() {
    syncAll();
    initHeroes();
    document.addEventListener('shopify:section:load', function (event) {
      initHeroes(event.target);
      syncAll();
    });
    /* Product grids are re-rendered by filters and "load more": keep hearts in sync. */
    if ('MutationObserver' in window) {
      new MutationObserver(function () {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(function () {
          scheduled = false;
          syncSavedUI();
        });
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.themeWall = { get: getWall, set: setWall };
})();
